import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap } from 'lucide-react';
import { API } from '../App';

const PREP_MESSAGES = [
  "Sharpening claws...",
  "Reviewing the numbers...",
  "Loading adversarial mandates...",
  "Calibrating friction parameters...",
  "Assembling committee positions...",
];

export default function VoiceReveal() {
  const { frayId } = useParams();
  const navigate = useNavigate();
  const [fray, setFray] = useState(null);
  const [revealed, setRevealed] = useState([]);
  const [prepMsg, setPrepMsg] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    API.get(`/api/frays/${frayId}`).then(r => setFray(r.data)).catch(() => navigate('/dashboard'));
  }, [frayId, navigate]);

  useEffect(() => {
    const interval = setInterval(() => setPrepMsg(p => (p + 1) % PREP_MESSAGES.length), 800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!fray?.voices) return;
    const voices = fray.voices;
    voices.forEach((_, i) => {
      setTimeout(() => setRevealed(prev => [...prev, i]), 600 + i * 200);
    });
    setTimeout(() => setDone(true), 600 + voices.length * 200 + 2000);
  }, [fray]);

  useEffect(() => {
    if (done) {
      const timeout = setTimeout(() => navigate(`/fray/${frayId}`), 1500);
      return () => clearTimeout(timeout);
    }
  }, [done, frayId, navigate]);

  if (!fray) return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const voices = fray.voices || [];
  const progress = Math.min((revealed.length / voices.length) * 100, 100);

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-4 md:px-6" data-testid="voice-reveal">
      <div className="w-full max-w-3xl">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-10">
          <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed'] mb-2" data-testid="reveal-heading">Your Committee</h1>
          <p className="text-[#A0A0A0] text-sm font-mono">{fray.template_name}</p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4 mb-10">
          <AnimatePresence>
            {voices.map((voice, i) => (
              revealed.includes(i) && (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  className="w-36 sm:w-44 p-3 sm:p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-center"
                  data-testid={`revealed-voice-${i}`}
                >
                  <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center border-2 text-xl font-bold font-['Barlow_Condensed']"
                    style={{ borderColor: voice.color, color: voice.color }}>
                    {voice.name.charAt(0)}
                  </div>
                  <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">{voice.name}</h3>
                  <p className="text-[10px] text-[#A0A0A0] leading-relaxed mb-2 line-clamp-2">{voice.mandate}</p>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 border"
                    style={{ color: voice.color, borderColor: voice.color + '40' }}>
                    {voice.model_tier?.toUpperCase()}
                  </span>
                </motion.div>
              )
            ))}
          </AnimatePresence>
        </div>

        {/* Progress bar */}
        <div className="max-w-md mx-auto">
          <div className="h-1 bg-[#2A2A2A] overflow-hidden mb-3">
            <motion.div
              className="h-full bg-[#FF4D00]"
              initial={{ width: '0%' }}
              animate={{ width: done ? '100%' : `${progress}%` }}
              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            />
          </div>
          <motion.p
            key={prepMsg}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-xs font-mono text-[#A0A0A0]"
          >
            {done ? 'Entering the Fray...' : PREP_MESSAGES[prepMsg]}
          </motion.p>
        </div>
      </div>
    </div>
  );
}
